import { Typography, Box, Paper, List, ListItem, ListItemIcon, ListItemText, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { AppLayout } from '../components/AppLayout';
import { AppHeader } from '../components/AppHeader';
import { AppFooter } from '../components/AppFooter';
import { TRUST_COPY, DATA_STORAGE } from '../constants/trustCopy';

/**
 * TransparencyPage - Plain-English explanation of what data is stored
 *
 * Implements Story 2.3: See what we store (and do not)
 * - Clear, plain-English summary of data handling
 * - Conforms to Trust Copy Checklist
 * - Accessible structure (headings, lists, links)
 * - No legal jargon or surveillance framing
 */
export function TransparencyPage() {
  return (
    <AppLayout header={<AppHeader />} footer={<AppFooter />}>
      <Box sx={{ py: 4 }}>
        <Link
          component={RouterLink}
          to="/"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            mb: 3,
            color: 'text.secondary',
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          <ArrowBackIcon fontSize="small" />
          Back to home
        </Link>

        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 300 }}>
          What we store (and do not)
        </Typography>

        <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary', maxWidth: '60ch' }}>
          {TRUST_COPY.TRANSPARENCY_INTRO}
        </Typography>

        {/* What we store */}
        <Paper elevation={0} sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2, mb: 3 }}>
          <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 500 }}>
            What we store
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            When you join, we keep only what is needed to match you with a colleague:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckCircleOutlineIcon color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={DATA_STORAGE.STORED.EMAIL.primary}
                secondary={DATA_STORAGE.STORED.EMAIL.secondary}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckCircleOutlineIcon color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={DATA_STORAGE.STORED.NAME.primary}
                secondary={DATA_STORAGE.STORED.NAME.secondary}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckCircleOutlineIcon color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={DATA_STORAGE.STORED.STATUS.primary}
                secondary={DATA_STORAGE.STORED.STATUS.secondary}
              />
            </ListItem>
          </List>
        </Paper>

        {/* What we do NOT store */}
        <Paper elevation={0} sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2, mb: 3 }}>
          <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 500 }}>
            What we do not store
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            We do not store anything that could be used to monitor or evaluate you:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <HighlightOffIcon color="error" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={DATA_STORAGE.NOT_STORED.RESPONSE_TRACKING.primary}
                secondary={DATA_STORAGE.NOT_STORED.RESPONSE_TRACKING.secondary}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <HighlightOffIcon color="error" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={DATA_STORAGE.NOT_STORED.CONVERSATION_TRACKING.primary}
                secondary={DATA_STORAGE.NOT_STORED.CONVERSATION_TRACKING.secondary}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <HighlightOffIcon color="error" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={DATA_STORAGE.NOT_STORED.ACTIVITY_PATTERNS.primary}
                secondary={DATA_STORAGE.NOT_STORED.ACTIVITY_PATTERNS.secondary}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <HighlightOffIcon color="error" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={DATA_STORAGE.NOT_STORED.PERSONAL_DETAILS.primary}
                secondary={DATA_STORAGE.NOT_STORED.PERSONAL_DETAILS.secondary}
              />
            </ListItem>
          </List>
        </Paper>

        {/* Privacy assurance */}
        <Paper elevation={0} sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2, mb: 3 }}>
          <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 500 }}>
            No manager visibility
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {TRUST_COPY.NO_MANAGER_VISIBILITY}
          </Typography>
        </Paper>

        {/* Can I leave? */}
        <Paper elevation={0} sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2, mb: 3 }}>
          <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 500 }}>
            Can I leave?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {TRUST_COPY.FAQ_CAN_I_LEAVE}
          </Typography>
        </Paper>

        {/* Final reassurance */}
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '60ch' }}>
          {TRUST_COPY.FULL_REASSURANCE}
        </Typography>
      </Box>
    </AppLayout>
  );
}
